<template>
  <div class="nav-root">
    <header class="nav" :class="{ 'nav--scrolled': isScrolled }">
      <div class="nav__inner">
        <router-link to="/" class="brand" aria-label="Salon Zap home">
          <span class="brand__mark"><span class="brand__pole" /></span>
          <span class="brand__text">
            <span class="brand__name">Salon <em>Zap</em></span>
            <span class="brand__tag">Booking &amp; business suite</span>
          </span>
        </router-link>

        <nav class="nav__links" aria-label="Main">
          <router-link to="/" class="nav__link" exact>Home</router-link>
          <router-link to="/why" class="nav__link">Why Salon Zap</router-link>
          <router-link to="/features" class="nav__link">Features</router-link>
          <router-link to="/about" class="nav__link">About</router-link>
          <router-link to="/blog" class="nav__link">Blog</router-link>
          <router-link to="/tribzz" class="nav__link">Tribzz</router-link>
        </nav>

        <div class="nav__actions">
          <router-link to="/login" class="nav__login">Login</router-link>
          <router-link to="/demo" class="nav__demo">Request Demo</router-link>
        </div>

        <button class="nav__burger" aria-label="Open menu" @click="drawer = true">
          <v-icon color="#1D2233">mdi-menu</v-icon>
        </button>
      </div>
    </header>

    <!-- Drawer is a sibling of <header>: backdrop-filter on the header would
         otherwise trap this position:fixed overlay inside the header's height. -->
    <transition name="drawer">
      <div v-if="drawer" class="overlay" @click="drawer = false">
        <aside class="drawer" @click.stop>
          <div class="drawer__head">
            <span class="brand__mark"><span class="brand__pole" /></span>
            <span class="brand__name">Salon <em>Zap</em></span>
            <button class="drawer__close" aria-label="Close menu" @click="drawer = false">
              <v-icon size="20" color="#5D5A55">mdi-close</v-icon>
            </button>
          </div>
          <nav class="drawer__nav">
            <a v-for="l in links" :key="l.to" class="drawer__link" :class="{ 'is-on': isActive(l.to) }" @click="go(l.to)">
              <v-icon size="20">{{ l.icon }}</v-icon>{{ l.label }}
            </a>
          </nav>
          <div class="drawer__foot">
            <button class="drawer__btn drawer__btn--ghost" @click="go('/login')">Login</button>
            <button class="drawer__btn" @click="go('/demo')">Request Demo</button>
          </div>
        </aside>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "SalonNavigation",
  data() {
    return {
      drawer: false,
      isScrolled: false,
      links: [
        { to: "/", label: "Home", icon: "mdi-home-outline" },
        { to: "/why", label: "Why Salon Zap", icon: "mdi-star-four-points-outline" },
        { to: "/features", label: "Features", icon: "mdi-view-grid-outline" },
        { to: "/about", label: "About", icon: "mdi-information-outline" },
        { to: "/blog", label: "Blog", icon: "mdi-post-outline" },
        { to: "/tribzz", label: "Tribzz for your shop", icon: "mdi-account-group-outline" },
      ],
    };
  },
  watch: {
    drawer(open) {
      document.body.style.overflow = open ? "hidden" : "";
    },
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    document.body.style.overflow = "";
  },
  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 12;
    },
    isActive(to) {
      return to === "/" ? this.$route.path === "/" : this.$route.path.startsWith(to);
    },
    go(to) {
      this.drawer = false;
      if (this.$route.path !== to) this.$router.push(to).catch(() => {});
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600;1,9..144,500&family=DM+Sans:wght@400;500;600;700&display=swap");

.nav-root {
  display: contents;
}

.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: rgba(247, 242, 234, 0.86);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid transparent;
  font-family: "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}

.nav--scrolled {
  background: rgba(247, 242, 234, 0.96);
  border-bottom-color: #e8dfd2;
  box-shadow: 0 10px 30px -24px rgba(29, 34, 51, 0.45);
}

.nav__inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 24px;
}

/* Brand */
.brand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #1d2233;
  flex: 0 0 auto;
}

.brand__mark {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #161c2c;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex: 0 0 auto;
}

.brand__pole {
  width: 10px;
  height: 26px;
  border-radius: 6px;
  background: repeating-linear-gradient(-45deg, #b23a33 0 4px, #f4efe6 4px 8px, #2f4a7a 8px 12px, #f4efe6 12px 16px);
  background-size: 100% 22px;
  animation: pole 2.6s linear infinite;
}

.brand__text {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.brand__name {
  font-family: "Fraunces", Georgia, serif;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #1d2233;
}

.brand__name em {
  font-style: italic;
  font-weight: 500;
  color: #b23a33;
}

.brand__tag {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8c8378;
}

/* Links */
.nav__links {
  display: flex;
  align-items: center;
  gap: 2px;
  margin: 0 auto;
}

.nav__link {
  position: relative;
  padding: 10px 14px;
  font-size: 14.5px;
  font-weight: 600;
  color: #5d5a55;
  text-decoration: none;
  border-radius: 999px;
  white-space: nowrap;
  transition: color 0.15s ease, background 0.15s ease;
}

.nav__link:hover {
  color: #1d2233;
  background: rgba(29, 34, 51, 0.05);
}

.nav__link.router-link-active {
  color: #1d2233;
}

.nav__link.router-link-active::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 3px;
  width: 5px;
  height: 5px;
  margin-left: -2.5px;
  border-radius: 50%;
  background: #b23a33;
}

/* Actions */
.nav__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.nav__login,
.nav__demo {
  display: inline-flex;
  align-items: center;
  height: 42px;
  padding: 0 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.nav__login {
  color: #1d2233;
  border: 1px solid #d6cab8;
}

.nav__login:hover {
  border-color: #1d2233;
}

.nav__demo {
  background: #1d2233;
  color: #fff;
}

.nav__demo:hover {
  background: #b23a33;
}

.nav__burger {
  display: none;
  margin-left: auto;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid #e8dfd2;
  background: #fffdf9;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* Drawer */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(22, 28, 44, 0.45);
  backdrop-filter: blur(4px);
  font-family: "DM Sans", sans-serif;
}

.drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(320px, 86vw);
  background: #fffdf9;
  border-left: 1px solid #e8dfd2;
  display: flex;
  flex-direction: column;
  padding: 18px;
  overflow-y: auto;
}

.drawer__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  border-bottom: 1px solid #efe7db;
}

.drawer__close {
  margin-left: auto;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid #e8dfd2;
  background: #f7f2ea;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.drawer__nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 14px 0;
}

.drawer__link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 14px;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  color: #5d5a55;
  cursor: pointer;
}

.drawer__link .v-icon {
  color: inherit !important;
}

.drawer__link:hover,
.drawer__link.is-on {
  background: #f7f2ea;
  color: #1d2233;
}

.drawer__link.is-on {
  box-shadow: inset 3px 0 0 #b23a33;
}

.drawer__foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.drawer__btn {
  height: 48px;
  border-radius: 999px;
  border: none;
  background: #1d2233;
  color: #fff;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
}

.drawer__btn--ghost {
  background: transparent;
  border: 1px solid #d6cab8;
  color: #1d2233;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.22s ease;
}
.drawer-enter-active .drawer,
.drawer-leave-active .drawer {
  transition: transform 0.25s ease;
}
.drawer-enter,
.drawer-leave-to {
  opacity: 0;
}
.drawer-enter .drawer,
.drawer-leave-to .drawer {
  transform: translateX(100%);
}

@keyframes pole {
  from { background-position: 0 0; }
  to { background-position: 0 22px; }
}

@media (prefers-reduced-motion: reduce) {
  .brand__pole { animation: none; }
}

@media (max-width: 1040px) {
  .nav__link { padding: 10px 10px; font-size: 14px; }
  .brand__tag { display: none; }
}

@media (max-width: 960px) {
  .nav__links,
  .nav__actions {
    display: none;
  }
  .nav__burger {
    display: flex;
  }
  .nav__inner {
    padding: 12px 16px;
  }
}
</style>